import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";

const ENV_API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://localhost:8000";
const API = `${ENV_API_BASE_URL}/api/testimonial-heading`;

const EMPTY_FORM = {
  mainHeading: "",
  subHeading: "",
};

export default function HomeTestinomialHeading() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [currentHeading, setCurrentHeading] = useState(null);
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [toast, setToast] = useState(null);

  const showToast = (msg, type = "success") => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3200);
  };

  const fetchHeading = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(API);
      const latest = Array.isArray(res.data?.data) ? res.data.data[0] : null;
      setCurrentHeading(latest || null);
      setFormData({
        mainHeading: latest?.mainHeading || "",
        subHeading: latest?.subHeading || "",
      });
    } catch (error) {
      console.error("Failed to load testimonial heading:", error);
      showToast("Failed to load heading data", "error");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchHeading();
  }, [fetchHeading]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.mainHeading.trim() || !formData.subHeading.trim()) {
      showToast("Main heading and sub heading are required", "error");
      return;
    }

    setSaving(true);
    try {
      if (currentHeading?._id) {
        await axios.put(`${API}/${currentHeading._id}`, {
          mainHeading: formData.mainHeading.trim(),
          subHeading: formData.subHeading.trim(),
        });
        showToast("Heading updated successfully");
      } else {
        await axios.post(API, {
          mainHeading: formData.mainHeading.trim(),
          subHeading: formData.subHeading.trim(),
        });
        showToast("Heading created successfully");
      }

      fetchHeading();
    } catch (error) {
      console.error("Failed to save testimonial heading:", error);
      showToast(
        error?.response?.data?.message || "Failed to save heading",
        "error"
      );
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!currentHeading?._id) return;
    try {
      await axios.delete(`${API}/${currentHeading._id}`);
      setCurrentHeading(null);
      setFormData(EMPTY_FORM);
      showToast("Heading deleted");
    } catch (error) {
      console.error("Failed to delete testimonial heading:", error);
      showToast(
        error?.response?.data?.message || "Failed to delete heading",
        "error"
      );
    }
  };

  return (
    <div className="container pt-5 mt-5">
      <div className="card shadow-sm border-0">
        <div className="card-body p-4 p-md-5">
          <div className="d-flex justify-content-between align-items-start flex-wrap gap-3 mb-4">
            <div>
              <p className="text-uppercase text-muted fw-semibold mb-2" style={{ letterSpacing: "0.12em", fontSize: 12 }}>
                Homepage Testimonials
              </p>
              <h2 className="mb-2" style={{ color: "#8d1f1f" }}>Heading Content</h2>
              <p className="text-muted mb-0">
                This controls the section title and subtitle shown above the testimonial slider on the homepage.
              </p>
            </div>
            {currentHeading && (
              <span className="badge text-bg-light border px-3 py-2">
                Live Content
              </span>
            )}
          </div>

          {loading ? (
            <div className="text-center py-5 text-muted">Loading heading content...</div>
          ) : (
            <>
              <div className="row g-4 mb-4">
                <div className="col-12">
                  <div
                    className="rounded-4 border p-4 text-center"
                    style={{
                      background:
                        "linear-gradient(135deg, rgba(255,248,246,1) 0%, rgba(255,255,255,1) 40%, rgba(252,241,241,1) 100%)",
                    }}
                  >
                    <h3
                      className="mb-3"
                      style={{
                        color: "#b32720",
                        fontFamily: "Philosopher, sans-serif",
                        fontSize: "clamp(2rem, 4vw, 3rem)",
                      }}
                    >
                      {formData.mainHeading || "Loved by Our Community"}
                    </h3>
                    <p
                      className="mx-auto mb-0"
                      style={{
                        maxWidth: 760,
                        color: "#333",
                        fontFamily: "Quicksand, sans-serif",
                        fontSize: "clamp(1rem, 2vw, 1.25rem)",
                        lineHeight: 1.6,
                      }}
                    >
                      {formData.subHeading ||
                        "Thousands of people around the world trust Hatha Yog for mindful movement and inner balance."}
                    </p>
                  </div>
                </div>
              </div>

              <form onSubmit={handleSubmit}>
                <div className="mb-4">
                  <label className="form-label fw-semibold">Main Heading</label>
                  <input
                    type="text"
                    name="mainHeading"
                    className="form-control form-control-lg"
                    value={formData.mainHeading}
                    onChange={handleChange}
                    placeholder="Loved by Our Community"
                    required
                  />
                </div>

                <div className="mb-4">
                  <label className="form-label fw-semibold">Sub Heading</label>
                  <textarea
                    name="subHeading"
                    className="form-control"
                    rows="4"
                    value={formData.subHeading}
                    onChange={handleChange}
                    placeholder="Thousands of people around the world trust Hatha Yog for mindful movement and inner balance."
                    required
                  />
                  <div className="form-text">
                    This text appears directly under the testimonial heading on the homepage.
                  </div>
                </div>

                <div className="d-flex flex-wrap gap-2">
                  <button className="btn btn-success px-4" disabled={saving}>
                    {saving ? "Saving..." : currentHeading ? "Update Heading" : "Save Heading"}
                  </button>
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={() =>
                      setFormData({
                        mainHeading: currentHeading?.mainHeading || "",
                        subHeading: currentHeading?.subHeading || "",
                      })
                    }
                    disabled={saving}
                  >
                    Reset
                  </button>
                  {currentHeading && (
                    <button
                      type="button"
                      className="btn btn-outline-danger"
                      onClick={handleDelete}
                      disabled={saving}
                    >
                      Delete
                    </button>
                  )}
                </div>
              </form>
            </>
          )}
        </div>
      </div>

      {toast && (
        <div
          className={`toast show position-fixed bottom-0 end-0 m-4 align-items-center text-white border-0 ${
            toast.type === "error" ? "bg-danger" : "bg-success"
          }`}
          role="status"
          aria-live="polite"
          aria-atomic="true"
          style={{ zIndex: 1080 }}
        >
          <div className="d-flex">
            <div className="toast-body">{toast.msg}</div>
            <button
              type="button"
              className="btn-close btn-close-white me-2 m-auto"
              onClick={() => setToast(null)}
            />
          </div>
        </div>
      )}
    </div>
  );
}
